import { createTheme } from '@mui/material/styles';

// Базовые цвета
const colors = {
  primary: {
    main: '#4F39F6',
    light: '#42a5f5',
    dark: '#1565c0',
    contrastText: '#ffffff',
  },
  secondary: {
    dark: '#A1B0BD',
    main: '#A1B0BD',
    light: '#A1B0BD',
    contrastText: '#ffffff',
  },
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
  },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// Настройки типографики
const typography = {
  fontFamily: '"Lato"',
  h1: {
    fontSize: '25px',
    fontWeight: 700,
    lineHeight: 1.2,
    color: "#323232",
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 500,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.6,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.43,
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
  },
};

// Настройки компонентов
const components = {
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        fontFamily: "'Lato' !important",
      },
      body: {
        fontFamily: "'Lato' !important",
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        padding: '10px 16px',
        borderRadius: 4,
        textTransform: 'none',
        fontSize: 14,
        lineHeight: "20px",
        fontWeight: 550,
      },
      sizeMedium: {
        height: 40,
      },
      sizeSmall: {
        height: "36px !important",
        minHeight: "36px !important",
        padding: '7px 16px',
      },
      contained: {
        backgroundColor: "#4F39F6",
        boxShadow: "none",
        '&:hover': {
          backgroundColor: "#4330D4",
          boxShadow: "none",
        },
      },
      containedSecondary: {
        backgroundColor: "#E0E7FF",
        color: "#432DD7",
        '&:hover': {
          backgroundColor: "#C7D2FE",
          boxShadow: "none",
        },
      },
      outlined: {
        borderColor: "#D4D4D8",
        color: "#323232",
        '&:hover': {
          borderColor: "#4F39F6",
          color: "#4F39F6",
          backgroundColor: "transparent",
        },
      },
      text: {
        color: "#4F39F6",
        '&:hover': {
          backgroundColor: "rgba(79, 57, 246, 0.08)",
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 4,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
      },
      colorSecondary: {
        backgroundColor: "#E0E7FF",
        color: "#432DD7",
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: {
        padding: 4,
        "& .MuiSvgIcon-root": {
          fontSize: 28,
          borderRadius: 2,
        },
        color: "#E4E4E7",
        "&.Mui-checked, &.MuiCheckbox-indeterminate": {
          color: "#635EF2",
        },
      },
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        zIndex: 1400,
        backgroundColor: "rgba(0, 0, 0, 0.2)",
      }
    }
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        "&:not(.--default-label):not(.--floating-label) fieldset": {
          top: 0
        },
        "&:not(.--default-label):not(.--floating-label) fieldset legend": {
          display: "none!important",
        },
        "&:not(.--default-label):not(.--floating-label) .MuiFormLabel-root": {
          position: 'initial',
          transform: 'initial !important',
          margin: '0 0 4px 10px',
        },
        "&.--floating-label fieldset legend": {
          display: "none !important",
        },
        "&.--floating-label fieldset": {
          top: 0,
        },
        "&.--floating-label .MuiFormLabel-root": {
          fontSize: 14,
          color: "#A1A1AA",
          transform: "translate(12px, 12px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(12px, 4px) scale(0.75)",
          },
          "&.Mui-focused": {
            color: "#71717A",
          },
        },
        "&.--floating-label .MuiOutlinedInput-root": {
          "& input": {
            paddingTop: 18,
            paddingBottom: 4,
          },
        }
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: "4px !important",
        minHeight: 40,
        fontSize: 14,
        lineHeight: "20px",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#D4D4D8",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#A1A1AA",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#635EF2",
        },
      },
      input: {
        padding: "10px 12px",
        fontSize: 14,
        lineHeight: "20px",
      },
      inputSizeSmall: {
        padding: "7px 10px",
      },
      sizeSmall: {
        minHeight: 36,
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        padding: "10px 12px",
        fontSize: 14,
        lineHeight: "20px",
        "&.MuiInputBase-inputSizeSmall": {
          padding: "7px 10px",
        },
      },
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      root: {
        borderRadius: "4px !important",
        minHeight: 40,
        fontSize: 14,
        lineHeight: "20px",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#D4D4D8",
        },
        "&.MuiInputBase-sizeSmall": {
          minHeight: 36,
        },
        "&.MuiInputBase-sizeSmall input": {
          padding: "7px 10px",
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      tag: {
        height: 24,
        margin: "0 4px"
      }
    }
  },

  MuiTable: {
    styleOverrides: {
      root: {
        borderCollapse: 'separate',
        borderSpacing: 0,
        width: '100%',
        border: '1px solid #E4E4E7',
        borderRadius: 8,
        overflow: 'hidden',
      }
    }
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        backgroundColor: '#FFF',
        borderBottom: '1px solid #E4E4E7',
        borderRight: '1px solid #E4E4E7',
        padding: '12px 16px',
        color: '#323232',
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: "550",
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        '&:last-child': {
          borderRight: 'none',
        },
      },
      body: {
        borderBottom: '1px solid #E4E4E7',
        borderRight: '1px solid #E4E4E7',
        padding: '10px 16px',
        fontSize: 14,
        color: "#323232",
        lineHeight: "18px",
        '&:last-child': {
          borderRight: 'none',
        },
      }
    }
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&.MuiTableRow-hover": {
          cursor: "pointer",
          transition: "background-color 0.2s"
        },
        "&.MuiTableRow-hover:hover": {
          backgroundColor: "#FAFAFA",
          "& .MuiTableCell-root": {
            backgroundColor: "#FAFAFA",
          },
        },
      },
    },
  },
  MuiTableBody: {
    styleOverrides: {
     root: {
       "& .MuiTableRow-root .MuiTableCell-root": {
         backgroundColor: '#FFF',
       },
       "& .MuiTableRow-root.--selected .MuiTableCell-root": {
         backgroundColor: '#E0E7FF !important',
       },
       "& .MuiTableRow-root.--selected .MuiTableCell-root:first-of-type": {
         borderLeft: '4px solid #432DD7',
       },
       "& .MuiTableRow-root:last-child .MuiTableCell-root": {
         borderBottom: "none !important",
       }
     }
    }
  },

  MuiPagination: {
    defaultProps: {
      shape: "rounded",
      showFirstButton: true,
      showLastButton: true,
      siblingCount: 2,
    },
    styleOverrides: {
      ul: {
        gap: 4,
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        minWidth: 32,
        height: 32,
        margin: 0,
        color: "#71717A",
        border: "1px solid #E4E4E7",
        borderRadius: 8,
        fontSize: 14,
        "&.Mui-selected": {
          backgroundColor: "#635EF2",
          color: "#fff",
          border: "1px solid #635EF2",
          "&:hover": {
            backgroundColor: "#4F46E5",
          },
        },
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      root: {
        zIndex: 1200,
      },
      paper: {
        borderRadius: 8,
        boxShadow: "0px 16px 32px -4px rgba(22, 34, 51, 0.12), 0px 4px 8px -4px rgba(22, 34, 51, 0.08)",
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 20px",
        fontSize: 18,
        color: "#27272A",
        fontWeight: 600,
        lineHeight: "24px",
        borderBottom: "1px solid #E4E4E7",
        "& .MuiIconButton-root": {
          padding: 4,
          color: "#71717A",
        },
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: "16px 20px",
        paddingTop: "16px !important",
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: "12px 20px",
        borderTop: "1px solid #E4E4E7",
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        boxShadow: '0px -8px 21px -4px #16223308'
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      content: {
        "& .MuiTypography-root": {
          fontSize: 20,
          color: "#323232",
          lineHeight: "1.2",
          fontWeight: "600"
        }
      }
    }
  },
};

// Создание темы
export const theme = createTheme({
  palette: colors,
  typography,
  components,
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
});

export default theme;
export {
  colors
}
